/**
 * Condensed product-detail box behaviour: tab switching, Wunschgerät (unit) selection,
 * the "Details ansehen" modal trigger, keeping the Zubehör tab count in sync with the selected
 * accessories, and async-loading the remaining units into the rail.
 *
 * Exposed as window.initCondensedBoxes so the PDP can re-initialise after a dynamic content swap
 * (e.g. switching the OK class or the 100%-battery option re-renders the PDS containers via AJAX).
 */
import { initSwiper } from './carousel.js';

function initCondensedBox(box) {
  // Guard against re-binding handlers if init runs twice over the same (un-swapped) DOM.
  if (box.dataset.condensedInit === '1') {
    return;
  }
  box.dataset.condensedInit = '1';

  const tabs = box.querySelectorAll('[data-condensed-tab]');
  const panels = box.querySelectorAll('.js_condensed_box__panel');

  // "Auswahl aufheben" returns to the no-unit state, so remember what to restore: the buy bar's
  // base price and each add-to-basket button's original onclick (captured before any selection
  // rewires them to a specific unit).
  const statusElement = box.querySelector('.js_condensed_box__unit_status');
  const statusTitle = box.querySelector('.js_condensed_box__unit_status_title');
  const statusText = box.querySelector('.js_condensed_box__unit_status_text');
  const deselectButton = box.querySelector('.js_condensed_box__deselect');
  const statusTitleFallback = statusTitle ? statusTitle.textContent.trim() : '';
  const statusTextFallback = statusText ? statusText.textContent.trim() : '';
  const addButtons = Array.from(document.querySelectorAll('.js_buybox_add_button'));
  const initialAddOnclick = addButtons.map(function (button) {
    return button.getAttribute('onclick');
  });
  const initialBasePrice = typeof window.basePrice !== 'undefined' && window.basePrice > 0 ? window.basePrice : null;

  // carousel.js's auto-init is tree-shaken from this bundle (we only import initSwiper), so
  // initialise the rail swipers here. Hidden panels init at zero width and self-correct via the
  // swiper observer once their tab becomes visible.
  box.querySelectorAll('.js_carousel__swiper').forEach(function (railSwiper) {
    if (!railSwiper.swiper) {
      initSwiper(railSwiper);
    }
  });

  box.addEventListener('click', function (event) {
    // "Details ansehen" opens the Einzelstück detail popup for that unit — it must not also select
    // the card.
    const detailsButton = event.target.closest('.js_unit_card__details');
    if (detailsButton) {
      if (typeof window.openUnitDetail === 'function') {
        window.openUnitDetail(
          box.dataset.atid,
          box.dataset.ok,
          detailsButton.dataset.articleId,
          box.dataset.newbat,
          box.dataset.vatonly,
        );
      }
      return;
    }

    if (event.target.closest('.js_condensed_box__deselect')) {
      deselectUnit();
      return;
    }

    const unitCard = event.target.closest('.js_unit_card');
    if (unitCard) {
      if (unitCard.classList.contains('unit_card--selected')) {
        deselectUnit();
        return;
      }
      box.querySelectorAll('.js_unit_card').forEach(function (card) {
        card.classList.remove('unit_card--selected');
      });
      unitCard.classList.add('unit_card--selected');
      selectUnit(unitCard.dataset.articleId || null, unitCard.dataset.price);
      return;
    }
  });

  // A chosen Wunschgerät drives the purchase: its price feeds the shared updatePrice() (which
  // refreshes the main and sticky prices plus the trade-in/PayPal figures), and both add-to-basket
  // buttons switch to adding this exact article — the selected accessories ride along via
  // buybox.testAvailable()'s Zubehör params.
  function selectUnit(articleId, priceText) {
    window.selectedUnitArticleId = articleId;

    const price = Number(priceText);
    if (price > 0) {
      window.basePrice = price;
      if (typeof window.updatePrice === 'function') {
        window.updatePrice(document.querySelectorAll('.js_buybox_equipment_item.active'));
      }
    }

    if (articleId) {
      document.querySelectorAll('.js_buybox_add_button').forEach(function (button) {
        const newBattery = box.dataset.newbat === '1' ? 'true' : 'false';
        button.setAttribute('onclick', 'buybox.testAvailable(' + articleId + ', false, ' + newBattery + ')');
      });
    }

    updateBuySummary();
    updateStatus();
  }

  // Drop the chosen Wunschgerät: clear the selection, restore the default price and the original
  // add-to-basket behaviour, and fall back to the "günstigstes passendes" hint.
  function deselectUnit() {
    box.querySelectorAll('.js_unit_card').forEach(function (card) {
      card.classList.remove('unit_card--selected');
    });
    window.selectedUnitArticleId = null;

    if (initialBasePrice !== null) {
      window.basePrice = initialBasePrice;
      if (typeof window.updatePrice === 'function') {
        window.updatePrice(document.querySelectorAll('.js_buybox_equipment_item.active'));
      }
    }

    addButtons.forEach(function (button, index) {
      if (initialAddOnclick[index] === null) {
        button.removeAttribute('onclick');
      } else {
        button.setAttribute('onclick', initialAddOnclick[index]);
      }
    });

    updateBuySummary();
    updateStatus();
  }

  // Reflect the current selection in the status box below the rail: the unselected state reassures
  // that skipping is fine, the selected state (--selected) confirms the concrete device and reveals
  // "Auswahl aufheben".
  function updateStatus() {
    if (!statusElement) {
      return;
    }
    const selectedCard = box.querySelector('.unit_card--selected');
    if (!selectedCard) {
      statusElement.classList.remove('condensed_box__unit_status--selected');
      if (statusTitle) {
        statusTitle.textContent = statusTitleFallback;
      }
      if (statusText) {
        statusText.textContent = statusTextFallback;
      }
      if (deselectButton) {
        deselectButton.hidden = true;
      }
      return;
    }

    statusElement.classList.add('condensed_box__unit_status--selected');
    if (statusTitle) {
      statusTitle.textContent =
        'Ausgewählt: ' + (selectedCard.dataset.displayId || '#' + selectedCard.dataset.articleId);
    }
    if (statusText) {
      const parts = [];
      if (selectedCard.dataset.battery) {
        parts.push(selectedCard.dataset.battery + '% Akku');
      }
      if (box.dataset.zustand) {
        parts.push(box.dataset.zustand);
      }
      parts.push('genau dieses Gerät wird geliefert');
      statusText.textContent = parts.join(' · ');
    }
    if (deselectButton) {
      deselectButton.hidden = false;
    }
  }

  function updateBuySummary() {
    const selectedCard = box.querySelector('.unit_card--selected');
    const accessoryCount = document.querySelectorAll('.js_buybox_equipment_item.active').length;

    document.querySelectorAll('.js_buybox_unit_summary').forEach(function (element) {
      const segments = [];
      if (element.dataset.default) {
        segments.push(document.createTextNode(element.dataset.default));
      }
      // Skip the per-unit capacity under the 100% option; the default line already states it.
      if (selectedCard && selectedCard.dataset.battery && box.dataset.newbat !== '1') {
        segments.push(document.createTextNode(selectedCard.dataset.battery + ' % Akku'));
      }
      if (selectedCard && (selectedCard.dataset.displayId || selectedCard.dataset.articleId)) {
        const idLabel = document.createElement('strong');
        idLabel.className = 'buybox_unit_summary_id';
        idLabel.textContent = selectedCard.dataset.displayId || '#' + selectedCard.dataset.articleId;
        segments.push(idLabel);
      }
      if (accessoryCount > 0) {
        segments.push(document.createTextNode('+' + accessoryCount + ' Zubehör'));
      }
      element.textContent = '';
      segments.forEach(function (segment, index) {
        if (index > 0) {
          element.appendChild(document.createTextNode(' · '));
        }
        element.appendChild(segment);
      });
    });
  }

  updateStatus();
  updateBuySummary();
  loadRemainingUnits();

  window.updateBuySummary = updateBuySummary;

  // The PDP renders only the first few Wunschgerät units inline (to keep the page fast); fetch the
  // rest and append them to the rail's swiper. data-atid is only present on the live PDP.
  function loadRemainingUnits() {
    const atid = box.dataset.atid;
    const unitPanel = panels[0];
    if (!atid || !unitPanel) {
      return;
    }
    const swiperElement = unitPanel.querySelector('.js_carousel__swiper');
    const loaded = unitPanel.querySelectorAll('.js_unit_card').length;
    if (!swiperElement || loaded === 0) {
      return;
    }

    const params = new URLSearchParams({
      objective: 'getunitrail',
      atid: atid,
      ok: box.dataset.ok || '',
      offset: String(loaded),
      newbat: box.dataset.newbat || '',
      no_diff: box.dataset.vatonly || '',
    });
    fetch('/shopController.php?' + params.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (!data || !data.html || !swiperElement.swiper) {
          return;
        }
        // appendSlide (Manipulation module) registers the new slides with Swiper's internal slide
        // array and reflows — unlike a raw DOM insert, which leaves the rail's sizing/drag broken.
        const template = document.createElement('div');
        template.innerHTML = data.html;
        swiperElement.swiper.appendSlide(Array.from(template.children));
      })
      .catch(function () {
        /* non-critical: the inline units remain usable */
      });
  }

  // Tabs, footer arrows and footer dots all drive the same panel track: switching slides it by one
  // panel-width and keeps the tab bar, dots, footer label and arrow disabled-state in sync.
  const track = box.querySelector('.js_condensed_box__track');
  const dots = box.querySelectorAll('.js_condensed_box__dot');
  const footLabel = box.querySelector('.js_condensed_box__foot_label');
  const prevArrow = box.querySelector('.js_condensed_box__prev');
  const nextArrow = box.querySelector('.js_condensed_box__next');
  const tabLabels = Array.from(tabs).map(function (tab) {
    const label = tab.querySelector('.condensed_box__tab_label');
    return label ? label.textContent.trim() : '';
  });
  let activeTab = 0;

  function goToTab(index) {
    const target = Math.max(0, Math.min(panels.length - 1, index));
    activeTab = target;

    if (track) {
      track.style.transform = 'translateX(-' + (target * 100) / panels.length + '%)';
    }

    tabs.forEach(function (tab, position) {
      const isActive = position === target;
      tab.classList.toggle('condensed_box__tab--active', isActive);
      tab.classList.toggle('js_condensed_box__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    dots.forEach(function (dot, position) {
      dot.classList.toggle('condensed_box__dot--active', position === target);
    });

    if (footLabel) {
      const suffix = tabLabels[target] === 'Wunschgerät' ? ' wählen' : '';
      footLabel.textContent = target + 1 + ' / ' + panels.length + ' · ' + tabLabels[target] + suffix;
    }
    if (prevArrow) {
      prevArrow.disabled = target === 0;
    }
    if (nextArrow) {
      nextArrow.disabled = target === panels.length - 1;
    }

    // An off-screen panel can have a stale (zero-width) swiper; (re)initialise or reflow it on show.
    const swiperElement = panels[target].querySelector('.js_carousel__swiper');
    if (swiperElement) {
      if (swiperElement.swiper) {
        swiperElement.swiper.update();
      } else {
        initSwiper(swiperElement);
      }
    }
  }

  tabs.forEach(function (tab, position) {
    tab.addEventListener('click', function (event) {
      event.preventDefault();
      goToTab(position);
    });
  });

  dots.forEach(function (dot, position) {
    dot.addEventListener('click', function () {
      goToTab(position);
    });
  });

  if (prevArrow) {
    prevArrow.addEventListener('click', function () {
      goToTab(activeTab - 1);
    });
  }
  if (nextArrow) {
    nextArrow.addEventListener('click', function () {
      goToTab(activeTab + 1);
    });
  }

  goToTab(0);
}

function initCondensedBoxes() {
  document.querySelectorAll('.js_condensed_box').forEach(initCondensedBox);
}

initCondensedBoxes();
window.initCondensedBoxes = initCondensedBoxes;
